import axios from 'axios';
import { wrapAxiosInstance } from '../cache/modules/axiosFactory';
import { setupAuthInterceptor, retryWithBackoff } from '../common';
import { NAMESPACES } from '../configuration';

const BASE_URL = NAMESPACES.configuration_api.local_plan_payments;

const api = setupAuthInterceptor(axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
}));

// Cached API wrapper for this namespace/group
const cachedApi = wrapAxiosInstance(api, {
  namespace: 'configuration_api',
  group: 'local_plan_payments',
  defaultTtl: '2m',
  hashLargeSegments: true,
  emitMeta: true,
});

// Types
export interface LocalPlanPayment {
  local_plan_payment_id: number;
  local_plan_id: number;
  local_id: number;
  payment_amount: number;
  payment_date: string;
  days_since_last_payment?: number;
  created_by_user_id?: number;
  local_plans_payments_metadata?: any;
}

export interface CreatePaymentParams {
  amount: number;
  payment_method?: string;
  payment_date?: string;
  notes?: string;
  local_plans_payments_metadata?: any;
}

export interface PaymentStatus {
  local_id: number;
  days_until_payment: number;
  last_payment_date?: string;
  amount_due: number;
  month_day_billing: number;
  is_overdue: boolean;
  status: 'OK' | 'DUE_TODAY' | 'OVERDUE';
}

export interface BillStatus {
  local_id: number;
  days_until_billing: number;
  last_payment_date?: string;
  amount_due: number;
  month_day_billing: number;
  is_overdue: boolean;
  status: 'OK' | 'DUE_TODAY' | 'OVERDUE';
}

export interface DebtSummary {
  local_id: number;
  local_name: string;
  days_overdue: number;
  amount_due: number;
  last_payment_date?: string;
  month_day_billing: number;
}

// API Functions

/**
 * Get all local plan payments
 */
export const getAllLocalPlanPayments = async () => {
  return await retryWithBackoff(() =>
    cachedApi.get('/GetAllLocalPlanPayments')
  );
};

/**
 * Register a new payment for a local
 */
export const createLocalPlanPayment = async (localId: number, params: CreatePaymentParams) => {
  const res = await retryWithBackoff(() =>
    api.post(`/CreateLocalPlanPayment/${localId}`, params)
  );
  await cachedApi.clear();
  return res;
};

/**
 * Register a new payment using local plan ID
 */
export const createLocalPlanPaymentByPlanId = async (localPlanId: number, params: CreatePaymentParams) => {
  const res = await retryWithBackoff(() =>
    api.post(`/CreateLocalPlanPaymentByPlanId/${localPlanId}`, params)
  );
  await cachedApi.clear();
  return res;
};

/**
 * Get a specific payment by its ID
 */
export const getLocalPlanPaymentById = async (paymentId: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetLocalPlanPaymentById/${paymentId}`)
  );
};

/**
 * Get payment history for a local (cartola)
 */
export const getLocalPlanPaymentByLocalId = async (localId: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetLocalPlanPaymentByLocalId/${localId}`)
  );
};

/**
 * Get payment history using local plan ID
 */
export const getLocalPlanPaymentByPlanId = async (localPlanId: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetLocalPlanPaymentByPlanId/${localPlanId}`)
  );
};

/**
 * Get the last payment for a local
 */
export const getLastPaymentByLocalId = async (localId: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetLastPaymentByLocalId/${localId}`)
  );
};

/**
 * Get the last payment using local plan ID
 */
export const getLastPaymentByPlanId = async (localPlanId: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetLastPaymentByPlanId/${localPlanId}`)
  );
};

/**
 * Get billing status for a local (if they owe money)
 */
export const getBillStatusByLocalId = async (localId: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetBillStatusByLocalId/${localId}`)
  );
};

/**
 * Get billing status using local plan ID
 */
export const getBillStatusByPlanId = async (localPlanId: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetBillStatusByPlanId/${localPlanId}`)
  );
};

/**
 * Get list of all locals with overdue payments
 */
export const getAllLocalPlanPaymentDebts = async () => {
  return await retryWithBackoff(() =>
    cachedApi.get('/GetAllLocalPlanPaymentDebts')
  );
};

/**
 * Efficient endpoint to get payment status for a local.
 * Returns days_until_payment: positive if payment date is in the future, 0 if today, negative if overdue
 */
export const getPlanPaymentStatusByLocalId = async (localId: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetPlanPaymentStatusByLocalId/${localId}`)
  );
};

/**
 * Get payment status using local plan ID
 */
export const getPlanPaymentStatusByPlanId = async (localPlanId: number) => {
  return await retryWithBackoff(() =>
    cachedApi.get(`/GetPlanPaymentStatusByPlanId/${localPlanId}`)
  );
};

/**
 * Delete a local plan payment by its ID
 */
export const deleteLocalPlanPayment = async (paymentId: number) => {
  const res = await retryWithBackoff(() =>
    api.delete(`/DeleteLocalPlanPayment/${paymentId}`)
  );
  await cachedApi.clear();
  return res;
};
